import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

type CategoryAt = (clientX: number, clientY: number, fromCategoryId: string) => string | null;

/**
 * Lets a row dragged in the category sidebar move to another category by dropping it on that
 * category's rail icon. The rail says where its icons are; the sidebar's drag asks which icon is
 * under the pointer, and names the one to highlight. LayoutV2's CategoryRailCollapseProvider provides it around both.
 */
export interface CategoryRailRowDrop {
  /** The category whose rail icon is at a viewport point, if rows from `fromCategoryId` may move there. */
  categoryAt: CategoryAt;
  /** The rail's: how it finds its icons, or null once it unmounts. */
  setCategoryAt: (categoryAt: CategoryAt | null) => void;
  /** The icon a dragged row is over, which the rail highlights. */
  targetCategoryId: string | null;
  setTargetCategoryId: (categoryId: string | null) => void;
  /**
   * The category a row is being dragged out of, while the drag lasts, so the rail can outline
   * the icons that take it.
   */
  draggingFromCategoryId: string | null;
  setDraggingFromCategoryId: (categoryId: string | null) => void;
}

const CategoryRailRowDropContext = createContext<CategoryRailRowDrop | null>(null);

/** Null outside LayoutV2, where there is no rail to drop on. */
export const useCategoryRailRowDrop = () => useContext(CategoryRailRowDropContext);

export const CategoryRailRowDropProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const categoryAtRef = useRef<CategoryAt | null>(null);
  const [targetCategoryId, setTargetCategoryId] = useState<string | null>(null);
  const [draggingFromCategoryId, setDraggingFromCategoryId] = useState<string | null>(null);
  const categoryAt = useCallback<CategoryAt>(
    (clientX, clientY, fromCategoryId) =>
      categoryAtRef.current?.(clientX, clientY, fromCategoryId) ?? null,
    [],
  );
  const setCategoryAt = useCallback((next: CategoryAt | null) => {
    categoryAtRef.current = next;
  }, []);
  const value = useMemo(
    (): CategoryRailRowDrop => ({
      categoryAt,
      setCategoryAt,
      targetCategoryId,
      setTargetCategoryId,
      draggingFromCategoryId,
      setDraggingFromCategoryId,
    }),
    [categoryAt, draggingFromCategoryId, setCategoryAt, targetCategoryId],
  );

  return (
    <CategoryRailRowDropContext.Provider value={value}>
      {children}
    </CategoryRailRowDropContext.Provider>
  );
};
